import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useState,
} from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  telemetryProps,
  type TelemetryPropsInput,
} from "../../../instrumentation/gtm";

type QuantitySelectorProps = {
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
  disabled?: boolean;
  decreaseTelemetry?: TelemetryPropsInput;
  increaseTelemetry?: TelemetryPropsInput;
  /**
   * When provided, the value becomes a text field so the quantity can be typed
   * directly. Valid input updates the value immediately.
   */
  onValueChange?: (value: number) => void;
  /**
   * Called on blur or Enter with a valid typed value. Use for server mutations.
   */
  onValueCommit?: (value: number) => void;
  /** The server-confirmed quantity used to avoid redundant commits. */
  committedValue?: number;
};

export function QuantitySelector({
  value,
  onDecrease,
  onIncrease,
  disabled = false,
  decreaseTelemetry,
  increaseTelemetry,
  onValueChange,
  onValueCommit,
  committedValue = value,
}: QuantitySelectorProps) {
  const theme = useTheme();
  const isEditable = onValueChange !== undefined || onValueCommit !== undefined;
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraft = () => {
    const parsed = Number(draftValue);
    if (Number.isInteger(parsed) && parsed >= 1) {
      if (parsed !== committedValue) {
        onValueCommit?.(parsed);
      }
      setDraftValue(String(parsed));
    } else {
      setDraftValue(String(value));
    }
  };

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/[^0-9]/g, "");
    setDraftValue(next);
    const parsed = Number(next);
    if (Number.isInteger(parsed) && parsed >= 1 && parsed !== value) {
      onValueChange?.(parsed);
    }
  };

  const handleDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft();
    }
  };

  const handleDraftBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.parentElement?.contains(event.relatedTarget)
    ) {
      return;
    }

    commitDraft();
  };
  const borderColor = alpha(theme.palette.text.primary, 0.18);
  const hoverBorderColor = theme.palette.primary.main;
  const buttonColor = alpha(theme.palette.text.primary, 0.56);
  const valueColor = theme.palette.text.primary;

  const ROOMY_CONTAINER_QUERY = "@container (min-width: 300px)";

  const segmentSx = {
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: 0,
    padding: 0,
    fontFamily: "inherit",
    fontSize: 12,
    lineHeight: 1,
    [ROOMY_CONTAINER_QUERY]: {
      height: 26,
      fontSize: 15,
    },
  };

  return (
    <Box
      role="group"
      aria-label="Quantity selector"
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        border: "1px solid",
        borderColor,
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "transparent",
        transition: theme.transitions.create(["border-color", "box-shadow"], {
          duration: theme.transitions.duration.short,
        }),
        "&:hover, &:focus-within": {
          borderColor: hoverBorderColor,
        },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label="Decrease quantity"
        onClick={onDecrease}
        disabled={disabled || value <= 1}
        {...(decreaseTelemetry ? telemetryProps(decreaseTelemetry) : {})}
        sx={{
          ...segmentSx,
          width: 24,
          [ROOMY_CONTAINER_QUERY]: {
            ...segmentSx[ROOMY_CONTAINER_QUERY],
            width: 28,
          },
          cursor: "pointer",
          color: buttonColor,
          "&:disabled": {
            cursor: "default",
            opacity: 0.35,
          },
          "&:hover": {
            color: hoverBorderColor,
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: theme.palette.primary.main,
            outlineOffset: -2,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        -
      </Box>
      <Box
        component={isEditable ? "input" : "output"}
        {...(isEditable
          ? {
              type: "text",
              inputMode: "numeric",
              "aria-label": "Quantity",
              value: draftValue,
              disabled,
              onChange: handleDraftChange,
              onBlur: handleDraftBlur,
              onKeyDown: handleDraftKeyDown,
            }
          : {
              role: "status",
              "aria-live": "polite",
              "aria-atomic": "true",
            })}
        sx={{
          ...segmentSx,
          width: 32,
          [ROOMY_CONTAINER_QUERY]: {
            ...segmentSx[ROOMY_CONTAINER_QUERY],
            width: 36,
          },
          borderLeft: "1px solid",
          borderRight: "1px solid",
          borderColor,
          color: valueColor,
          fontVariantNumeric: "tabular-nums",
          ...(isEditable && {
            textAlign: "center",
            outline: "none",
            MozAppearance: "textfield",
            "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          }),
        }}
      >
        {isEditable ? undefined : value}
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="Increase quantity"
        onClick={onIncrease}
        disabled={disabled}
        {...(increaseTelemetry ? telemetryProps(increaseTelemetry) : {})}
        sx={{
          ...segmentSx,
          width: 24,
          [ROOMY_CONTAINER_QUERY]: {
            ...segmentSx[ROOMY_CONTAINER_QUERY],
            width: 28,
          },
          cursor: "pointer",
          color: buttonColor,
          "&:disabled": {
            cursor: "default",
            opacity: 0.35,
          },
          "&:hover": {
            color: hoverBorderColor,
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: theme.palette.primary.main,
            outlineOffset: -2,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        +
      </Box>
    </Box>
  );
}
